import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { supplierContacts } from "@/lib/vendorContacts";

const splitList = (s) =>
  String(s || "")
    .split(/[,;\s]+/)
    .map((x) => x.trim())
    .filter(Boolean);

// Email the return request + 4×6 labels (PDF) to the supplier.
export const RMAEmailDialog = ({ rma, supplier, open, onClose, onSent }) => {
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setTo(rma?.supplier_notified_to || supplier?.email || supplier?.rep_email || "");
      setCc("");
      setMessage("");
    }
  }, [open, rma, supplier]);

  const contacts = supplierContacts(supplier);
  const inList = (list, email) => splitList(list).some((x) => x.toLowerCase() === email.toLowerCase());
  const toggleCc = (email) => {
    const cur = splitList(cc);
    setCc(
      (inList(cc, email) ? cur.filter((x) => x.toLowerCase() !== email.toLowerCase()) : [...cur, email]).join(", "),
    );
  };

  const send = async (e) => {
    e.preventDefault();
    if (!to.includes("@")) {
      toast.error("Enter the supplier's email address");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/rmas/${rma.id}/email`, { to: to.trim(), cc, message });
      toast.success(`Return request emailed to ${data.email?.to || to}`);
      onSent?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send");
    }
    setBusy(false);
  };

  const sent = !!rma?.supplier_notified_at;

  const ContactChips = ({ target }) =>
    contacts.length ? (
      <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`rma-email-${target}-contacts`}>
        {contacts.map((c) => {
          const on = target === "to" ? to.trim().toLowerCase() === c.email.toLowerCase() : inList(cc, c.email);
          return (
            <button
              key={`${target}-${c.email}`}
              type="button"
              data-testid={`rma-email-${target}-contact`}
              onClick={() => (target === "to" ? setTo(c.email) : toggleCc(c.email))}
              title={c.email}
              className={`rounded-full border px-2 py-0.5 text-[11px] transition-colors duration-150 ${
                on
                  ? "border-amber-400/50 bg-amber-400/10 text-amber-200"
                  : "border-zinc-700 text-zinc-400 hover:border-zinc-500 hover:text-zinc-200"
              }`}
            >
              {on ? "✓ " : "+ "}
              {c.label}
            </button>
          );
        })}
      </div>
    ) : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="rma-email-dialog" className="max-w-lg border-zinc-800 bg-zinc-900">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PaperPlaneTilt size={18} weight="bold" className="text-indigo-400" />
            Email return {rma?.code} to the supplier
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            The request goes out with the 4×6 return labels attached as a PDF.
            {sent ? " Already sent once before." : ""}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={send} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">To</Label>
            <Input
              data-testid="rma-email-to"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="returns@supplier.com"
              className="h-12 border-zinc-800 bg-zinc-950 text-base"
            />
            <ContactChips target="to" />
            {!contacts.length && (
              <p className="text-xs text-zinc-500">
                No email saved on this supplier — add one on the supplier record to pre-fill next time.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Cc (optional)</Label>
            <Input
              data-testid="rma-email-cc"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              placeholder="rep@supplier.com, you@yourshop.com"
              className="h-12 border-zinc-800 bg-zinc-950 text-base"
            />
            <ContactChips target="cc" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Message (optional)</Label>
            <Textarea
              data-testid="rma-email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Please confirm your RMA number and the pickup date."
              className="border-zinc-800 bg-zinc-950 text-base"
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              data-testid="rma-email-send"
              disabled={busy}
              className="h-12 flex-1 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              {busy ? "Sending…" : sent ? "Send again" : "Send return request"}
            </Button>
            <Button
              type="button"
              data-testid="rma-email-cancel"
              variant="outline"
              onClick={onClose}
              className="h-12 border-zinc-700 bg-transparent text-xs uppercase tracking-widest"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
